import React, { useState } from "react";
import { gql } from "@apollo/client";
import {
  HtmlTable,
  HtmlTableBody,
  HtmlTableCell,
  HtmlTableHead,
  HtmlTableHeader,
  HtmlTableRow,
} from "@src/lib/ui/HtmlTable";
import { Skeleton } from "@src/lib/ui/Skeleton";
import {
  EmptyState,
  EmptyStateDescription,
  EmptyStateTitle,
} from "@src/lib/ui/EmptyState";
import Text from "@src/lib/ui/Text";
import { Badge } from "@src/lib/ui/Badge";
import { Button } from "@src/lib/ui/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@src/lib/ui/DropdownMenu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@src/lib/ui/AlertDialog";
import {
  useOrgGuestFeedbackIntegrationsQuery,
  useUpdateOrgGuestFeedbackIntegrationMutation,
  useDeleteOrgGuestFeedbackIntegrationMutation,
  OrgGuestFeedbackIntegrationFragment,
} from "./GuestFeedbackIntegrationsTab.generated";
import { useDetailSidebarContext } from "@src/lib/contexts/DetailSidebarContext";
import { DetailSidebarType } from "@src/features/sidebars/detailSidebar";
import LinkIcon from "@src/lib/icons/18px/link";
import DotsIcon from "@src/lib/icons/18px/dots";
import TrashOutlineIcon from "@src/lib/icons/18px/trash-outline";
import EyeOutlineIcon from "@src/lib/icons/18px/eye-outline";
import EyeSlashOutlineIcon from "@src/lib/icons/18px/eye-slash-outline";
import { useToast } from "@src/lib/hooks/useToast";
import { GraphqlOperations } from "@src/types.generated";

gql`
  fragment OrgGuestFeedbackIntegration on OrgGuestFeedbackIntegration {
    id
    enabled
    googleBusinessConfig {
      id
      accountId
    }
    locationGroup {
      id
      name
    }
    ovationIntegrationConfig {
      id
      companyId
    }
    tattleIntegrationConfig {
      id
      merchantId
    }
    locations {
      id
      sourceLocationId
      sourceName
      location {
        id
        name
      }
    }
  }

  query OrgGuestFeedbackIntegrations {
    OrgGuestFeedbackIntegrations {
      ...OrgGuestFeedbackIntegration
    }
  }

  mutation UpdateOrgGuestFeedbackIntegration(
    $input: UpdateOrgGuestFeedbackIntegrationInput!
  ) {
    updateOrgGuestFeedbackIntegration(input: $input) {
      success
      error {
        message
      }
      orgGuestFeedbackIntegration {
        id
        enabled
      }
    }
  }

  mutation DeleteOrgGuestFeedbackIntegration($id: UUID!) {
    deleteOrgGuestFeedbackIntegration(id: $id) {
      success
      error {
        message
      }
    }
  }
`;

const getIntegrationType = (
  integration: OrgGuestFeedbackIntegrationFragment,
): string => {
  if (integration.googleBusinessConfig) {
    return "Google Business";
  }
  if (integration.ovationIntegrationConfig) {
    return "Ovation";
  }
  if (integration.tattleIntegrationConfig) {
    return "Tattle";
  }
  return "Unknown";
};

const GuestFeedbackIntegrationsTab: React.FC = () => {
  const { addToast, addErrorToast } = useToast();
  const { _toggleSidebar } = useDetailSidebarContext();
  const { data, loading } = useOrgGuestFeedbackIntegrationsQuery();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [pendingDeleteIntegration, setPendingDeleteIntegration] =
    useState<OrgGuestFeedbackIntegrationFragment | null>(null);

  const [updateIntegration] = useUpdateOrgGuestFeedbackIntegrationMutation({
    onCompleted: (result) => {
      if (result.updateOrgGuestFeedbackIntegration.success) {
        const enabled =
          result.updateOrgGuestFeedbackIntegration.orgGuestFeedbackIntegration
            ?.enabled;
        addToast({
          message: `Integration ${enabled ? "enabled" : "disabled"}`,
        });
      } else {
        addErrorToast({
          message:
            result.updateOrgGuestFeedbackIntegration.error?.message ??
            "Failed to update integration",
          callsite: "guest_feedback_integration_update",
        });
      }
    },
  });

  const [deleteIntegration] = useDeleteOrgGuestFeedbackIntegrationMutation({
    refetchQueries: [
      GraphqlOperations.Query.OrgGuestFeedbackIntegrations,
      GraphqlOperations.Query.GuestFeedbackSetupStatus,
    ],
    awaitRefetchQueries: true,
    onCompleted: (result) => {
      if (result.deleteOrgGuestFeedbackIntegration.success) {
        addToast({ message: "Integration deleted" });
      } else {
        addErrorToast({
          message:
            result.deleteOrgGuestFeedbackIntegration.error?.message ??
            "Failed to delete integration",
          callsite: "guest_feedback_integration_delete",
        });
      }
    },
  });

  const integrations = data?.OrgGuestFeedbackIntegrations ?? [];

  const handleToggleEnabled = (
    e: React.MouseEvent,
    integration: OrgGuestFeedbackIntegrationFragment,
  ) => {
    e.stopPropagation();
    updateIntegration({
      variables: {
        input: {
          id: integration.id,
          enabled: !integration.enabled,
        },
      },
    });
  };

  const handleDelete = (
    e: React.MouseEvent,
    integration: OrgGuestFeedbackIntegrationFragment,
  ) => {
    e.stopPropagation();
    setPendingDeleteIntegration(integration);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (!pendingDeleteIntegration) return;
    deleteIntegration({
      variables: {
        id: pendingDeleteIntegration.id,
      },
    });
    setDeleteDialogOpen(false);
    setPendingDeleteIntegration(null);
  };

  const canDelete = (integration: OrgGuestFeedbackIntegrationFragment) => {
    return integration.ovationIntegrationConfig === null;
  };

  if (loading && !data) {
    return (
      <div className="flex flex-col gap-2 py-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full rounded" />
        ))}
      </div>
    );
  }

  if (integrations.length === 0) {
    return (
      <EmptyState>
        <EmptyStateTitle>No integrations configured</EmptyStateTitle>
        <EmptyStateDescription>
          Guest feedback integrations will appear here once configured.
        </EmptyStateDescription>
      </EmptyState>
    );
  }

  return (
    <>
      <div className="flex flex-col gap-4 py-4">
        <Text
          variant="fine-print-description"
          className="text-muted-foreground"
        >
          {integrations.length} integration
          {integrations.length !== 1 ? "s" : ""}
        </Text>

        <HtmlTable className="w-full">
          <HtmlTableHeader>
            <HtmlTableRow>
              <HtmlTableHead className="w-[200px]">
                Integration ID
              </HtmlTableHead>
              <HtmlTableHead className="w-[150px]">Type</HtmlTableHead>
              <HtmlTableHead className="w-[150px]">Brand</HtmlTableHead>
              <HtmlTableHead className="w-[100px]">Status</HtmlTableHead>
              <HtmlTableHead className="w-[100px]">Locations</HtmlTableHead>
              <HtmlTableHead className="w-[50px]" />
            </HtmlTableRow>
          </HtmlTableHeader>
          <HtmlTableBody>
            {integrations.map((integration) => (
              <HtmlTableRow
                key={integration.id}
                className="cursor-pointer"
                onClick={(e) =>
                  _toggleSidebar(
                    e,
                    integration.id,
                    DetailSidebarType.GuestFeedbackIntegration,
                    "guest-feedback-integrations",
                  )
                }
              >
                <HtmlTableCell>
                  <div className="flex items-center gap-2">
                    <LinkIcon className="text-gray-7" />
                    <Text variant="compact-title">{integration.id}</Text>
                  </div>
                </HtmlTableCell>
                <HtmlTableCell>
                  <Text variant="compact-description">
                    {getIntegrationType(integration)}
                  </Text>
                </HtmlTableCell>
                <HtmlTableCell>
                  <Text
                    variant="compact-description"
                    className="text-muted-foreground"
                  >
                    {integration.locationGroup?.name ?? "All locations"}
                  </Text>
                </HtmlTableCell>
                <HtmlTableCell>
                  <Badge
                    variant={integration.enabled ? "success" : "secondary"}
                  >
                    {integration.enabled ? "Enabled" : "Disabled"}
                  </Badge>
                </HtmlTableCell>
                <HtmlTableCell>
                  <Text
                    variant="compact-description"
                    className="text-muted-foreground"
                  >
                    {
                      integration.locations.filter(({ location }) => location)
                        .length
                    }{" "}
                    of {integration.locations.length} linked
                  </Text>
                </HtmlTableCell>
                <HtmlTableCell>
                  <DropdownMenu modal={false}>
                    <DropdownMenuTrigger
                      asChild
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Button variant="ghost" size="icon">
                        <DotsIcon />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" side="bottom">
                      <DropdownMenuItem
                        className="cursor-pointer"
                        onClick={(e) => handleToggleEnabled(e, integration)}
                      >
                        {integration.enabled ? (
                          <>
                            <EyeSlashOutlineIcon className="text-gray-10" />
                            <Text variant="compact-title">Disable</Text>
                          </>
                        ) : (
                          <>
                            <EyeOutlineIcon className="text-gray-10" />
                            <Text variant="compact-title">Enable</Text>
                          </>
                        )}
                      </DropdownMenuItem>
                      {canDelete(integration) && (
                        <DropdownMenuItem
                          className="cursor-pointer text-destructive"
                          onClick={(e) => handleDelete(e, integration)}
                        >
                          <TrashOutlineIcon />
                          <Text variant="compact-title">Delete</Text>
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </HtmlTableCell>
              </HtmlTableRow>
            ))}
          </HtmlTableBody>
        </HtmlTable>
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Integration</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this integration? This will also
              delete all reviews that have been synced from this integration.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel asChild>
              <Button variant="outline">Cancel</Button>
            </AlertDialogCancel>
            <AlertDialogAction asChild>
              <Button variant="destructive" onClick={confirmDelete}>
                Delete
              </Button>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default GuestFeedbackIntegrationsTab;
